(function () {
  'use strict';

  class MndfrmeHeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
      this.timer = null;
      this.pointerStart = null;
      this.touchStart = null;
      this.didSwipe = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this.controller.signal;

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.heroDot), true), { signal }));
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

      // Pointer events for mouse/pen (touch is handled separately below)
      this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal, passive: true });
      this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal, passive: true });
      this.addEventListener('pointercancel', () => { this.pointerStart = null; this.didSwipe = false; }, { signal, passive: true });

      // Touch events for reliable mobile swipe with scroll prevention
      this.addEventListener('touchstart', (event) => this.onTouchStart(event), { signal, passive: true });
      this.addEventListener('touchmove', (event) => this.onTouchMove(event), { signal, passive: false });
      this.addEventListener('touchend', (event) => this.onTouchEnd(event), { signal, passive: true });
      this.addEventListener('touchcancel', () => { this.touchStart = null; this.didSwipe = false; }, { signal, passive: true });

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      document.addEventListener('visibilitychange', () => this.onVisibilityChange(), { signal });
      this.motionQuery.addEventListener?.('change', () => this.onMotionChange(), { signal });
      this.goTo(this.index);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.controller?.abort();
      this.controller = null;
    }

    goTo(nextIndex, userInitiated = false) {
      if (!this.slides.length) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, index) => {
        slide.hidden = index !== this.index;
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (userInitiated) this.start();
    }

    start() {
      this.stop();
      if (this.slides.length < 2 || this.dataset.autoplay !== 'true' || this.motionQuery.matches || document.hidden) return;
      const interval = Number(this.dataset.interval) || 5000;
      this.timer = window.setInterval(() => this.goTo(this.index + 1), interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') this.goTo(this.index - 1, true);
      if (event.key === 'ArrowRight') this.goTo(this.index + 1, true);
    }

    onPointerDown(event) {
      if (event.pointerType === 'touch') return;
      this.pointerStart = { x: event.clientX, y: event.clientY };
      this.didSwipe = false;
    }

    onPointerUp(event) {
      if (event.pointerType === 'touch' || !this.pointerStart) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) { this.didSwipe = false; return; }
      this.didSwipe = true;
      this.goTo(this.index + (deltaX < 0 ? 1 : -1), true);
    }

    onTouchStart(event) {
      if (event.touches.length !== 1) return;
      const t = event.touches[0];
      this.touchStart = { x: t.clientX, y: t.clientY };
      this.didSwipe = false;
    }

    onTouchMove(event) {
      if (!this.touchStart || event.touches.length !== 1) return;
      const t = event.touches[0];
      const dx = t.clientX - this.touchStart.x;
      const dy = t.clientY - this.touchStart.y;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) event.preventDefault();
    }

    onTouchEnd(event) {
      if (!this.touchStart) return;
      const t = event.changedTouches[0];
      const dx = t.clientX - this.touchStart.x;
      const dy = t.clientY - this.touchStart.y;
      this.touchStart = null;
      if (Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy)) { this.didSwipe = false; return; }
      this.didSwipe = true;
      this.goTo(this.index + (dx < 0 ? 1 : -1), true);
    }

    onClick(event) {
      if (this.didSwipe) { event.preventDefault(); this.didSwipe = false; }
    }

    onVisibilityChange() {
      if (document.hidden) this.stop();
      else this.start();
    }

    onMotionChange() {
      if (this.motionQuery.matches) this.stop();
      else this.start();
    }
  }

  if (!customElements.get('mndfrme-hero-carousel')) {
    customElements.define('mndfrme-hero-carousel', MndfrmeHeroCarousel);
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelector('mndfrme-hero-carousel')?.connectedCallback();
  });
})();
